import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import type { FinancialPeriod, PeriodType, PrimaryDataSource } from '@/types/financial';

export interface PeriodFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityRecordId: string;
  period: FinancialPeriod | null; // null = create mode, object = edit mode
}

/**
 * Period Form Modal
 *
 * Supports both create and edit modes with:
 * - Native HTML select and input elements for Playwright compatibility
 * - Form validation (required fields, end date > start date)
 * - Pre-population in edit mode
 *
 * Test IDs:
 * - modal-add-period
 * - select-period-type
 * - input-period-label
 * - input-period-start-date
 * - input-period-end-date
 * - select-data-source
 * - button-save-period
 */
export const PeriodFormModal: React.FC<PeriodFormModalProps> = ({
  open,
  onOpenChange,
  entityRecordId,
  period,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [periodType, setPeriodType] = useState<PeriodType | ''>('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dataSource, setDataSource] = useState<PrimaryDataSource | ''>('');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!period;

  // Pre-populate form when editing
  useEffect(() => {
    if (open) {
      if (period) {
        setPeriodType(period.periodType);
        setPeriodLabel(period.periodLabel);
        setStartDate(period.periodStartDate);
        setEndDate(period.periodEndDate);
        setDataSource(period.dataSource || '');
      } else {
        // Reset form for create mode
        setPeriodType('');
        setPeriodLabel('');
        setStartDate('');
        setEndDate('');
        setDataSource('');
      }
      setErrors({});
    }
  }, [open, period]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', '/api/financial/periods', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'periods', entityRecordId] });
      toast({
        title: 'Period created',
        description: 'Financial period has been created successfully.',
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create period',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest('PATCH', `/api/financial/periods/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'periods', entityRecordId] });
      toast({
        title: 'Period updated',
        description: 'Financial period has been updated successfully.',
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update period',
        variant: 'destructive',
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!periodType) {
      newErrors.periodType = 'Period type is required';
    }

    if (!periodLabel.trim()) {
      newErrors.periodLabel = 'Period label is required';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Validate end date is after start date
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateMonths = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const months =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth()) +
      1;
    return Math.max(1, months);
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const monthsInPeriod = calculateMonths(startDate, endDate);

    const data = {
      entityFinancialRecordId: entityRecordId,
      periodType,
      periodLabel: periodLabel.trim(),
      periodStartDate: startDate,
      periodEndDate: endDate,
      monthsInPeriod,
      periodSequence: period?.periodSequence || 1,
      dataSource: dataSource || 'manual',
    };

    if (isEditMode && period) {
      updateMutation.mutate({ id: period.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="modal-add-period">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Financial Period' : 'Add Financial Period'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Period Type - Native select for Playwright compatibility */}
          <div className="space-y-2">
            <Label htmlFor="period-type">Period Type</Label>
            <select
              id="period-type"
              data-testid="select-period-type"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!errors.periodType}
            >
              <option value="">Select period type</option>
              <option value="historical">Historical</option>
              <option value="ytd">YTD</option>
              <option value="forecast">Forecast</option>
            </select>
            {errors.periodType && (
              <p className="text-sm text-destructive">{errors.periodType}</p>
            )}
          </div>

          {/* Period Label */}
          <div className="space-y-2">
            <Label htmlFor="period-label">Period Label</Label>
            <input
              type="text"
              id="period-label"
              data-testid="input-period-label"
              value={periodLabel}
              onChange={(e) => setPeriodLabel(e.target.value)}
              placeholder="e.g., FY2024, Q1 2024"
              maxLength={50}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!errors.periodLabel}
            />
            {errors.periodLabel && (
              <p className="text-sm text-destructive">{errors.periodLabel}</p>
            )}
          </div>

          {/* Start Date - Native date input for Playwright compatibility */}
          <div className="space-y-2">
            <Label htmlFor="start-date">Start Date</Label>
            <input
              type="date"
              id="start-date"
              data-testid="input-period-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!errors.startDate}
            />
            {errors.startDate && (
              <p className="text-sm text-destructive">{errors.startDate}</p>
            )}
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <Label htmlFor="end-date">End Date</Label>
            <input
              type="date"
              id="end-date"
              data-testid="input-period-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!errors.endDate}
            />
            {errors.endDate && (
              <p className="text-sm text-destructive">{errors.endDate}</p>
            )}
          </div>

          {/* Data Source - Optional */}
          <div className="space-y-2">
            <Label htmlFor="data-source">Data Source</Label>
            <select
              id="data-source"
              data-testid="select-data-source"
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value as PrimaryDataSource)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Select data source</option>
              <option value="manual">Manual Entry</option>
              <option value="accounting_xero">Xero</option>
              <option value="accounting_myob">MYOB</option>
              <option value="bank_feed">Bank Feed</option>
              <option value="ai_extracted">AI Extracted</option>
              <option value="document_parsed">Document Parsed</option>
            </select>
          </div>

          {/* Months preview */}
          {startDate && endDate && new Date(endDate) > new Date(startDate) && (
            <p className="text-xs text-muted-foreground">
              Period: {calculateMonths(startDate, endDate)} months
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending} data-testid="button-save-period">
            {isPending ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
